import { userError } from "@/lib/userError";
import React, { useState } from "react";
import { Phone, Plus, ShieldCheck, PhoneCall, Loader2 } from "lucide-react";
import {
  getContacts, createContact, updateContact, deleteContact,
  createApprovalToken, createCallRequest,
} from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { PageHeader, LoadingCards, ErrorState, EmptyState } from "@/components/Common";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ConfirmDeleteButton } from "@/components/ConfirmDeleteButton";

const PURPOSE_OPTIONS = ["kiireelliset", "strategiapalaverit", "laskutuskysymykset", "projektikoordinointi"];
const ROLE_OPTIONS = ["Kasvujohtaja", "Asiakas", "Tiimin jäsen", "Perhe", "Muu"];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const AddContactDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("Muu");
  const [purposes, setPurposes] = useState([]);

  const togglePurpose = (p) => setPurposes((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);

  const submit = async () => {
    if (!name || !phone) { toast.error("Täytä nimi ja numero."); return; }
    try {
      await createContact({ name, phone_e164: phone, role, allowed_purposes: purposes, enabled: true });
      toast.success("Yhteystieto lisätty sallittujen listalle.");
      setOpen(false); setName(""); setPhone(""); setRole("Muu"); setPurposes([]);
      onCreated();
    } catch (e) {
      toast.error(userError(e, "Lisääminen epäonnistui."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid="add-contact-btn" className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground active:scale-95 transition-transform">
          <Plus className="h-4 w-4" /> Lisää
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-sm mx-auto" data-testid="add-contact-dialog">
        <DialogHeader><DialogTitle className="font-heading">Lisää sallittu yhteystieto</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Nimi</Label><Input data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl mt-1" /></div>
          <div>
            <Label className="text-xs">Puhelinnumero maakoodilla</Label>
            <Input data-testid="contact-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+358401234567" className="rounded-xl mt-1 font-mono" />
            <p className="mt-1 text-xs text-muted-foreground">Esimerkiksi 040 123 4567 → +358401234567.</p>
          </div>
          <div>
            <Label className="text-xs">Rooli</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="contact-role" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Sallitut soiton tarkoitukset</Label>
            <div className="flex flex-wrap gap-2">
              {PURPOSE_OPTIONS.map((p) => (
                <button key={p} type="button" data-testid={`purpose-opt-${p}`} onClick={() => togglePurpose(p)} aria-pressed={purposes.includes(p)}
                  className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    purposes.includes(p) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground")}>
                  {cap(p)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button type="button" data-testid="contact-create-confirm" onClick={submit} className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground">Lisää listalle</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CallDialog = ({ contact }) => {
  const [open, setOpen] = useState(false);
  const [purpose, setPurpose] = useState(contact.allowed_purposes[0] || "");
  const [busy, setBusy] = useState(false);

  const call = async () => {
    if (!purpose) { toast.error("Valitse tarkoitus."); return; }
    setBusy(true);
    try {
      const { token } = await createApprovalToken({ action: "call", contact_id: contact.id, purpose });
      const res = await createCallRequest({ contact_id: contact.id, purpose, approval_token: token });
      toast.success(`Puhelu hyväksytty ja käynnistetty: ${res.resolved_number}`);
      setOpen(false);
    } catch (e) {
      const reason = userError(e, "Puhelua ei voitu hyväksyä. Tarkista yhteystiedon lupa ja hyväksynnän voimassaolo.");
      toast.error(`Estetty: ${reason}`);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid={`call-btn-${contact.id}`} disabled={!contact.enabled || contact.allowed_purposes.length === 0}
          className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary disabled:opacity-40 active:scale-95 transition-transform">
          <PhoneCall className="h-3.5 w-3.5" /> Pyydä puhelu
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-sm mx-auto" data-testid={`call-dialog-${contact.id}`}>
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Vahvista puhelu</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Soitto: <span className="text-foreground font-medium">{contact.name}</span> ({contact.phone_e164})</p>
          <div>
            <Label className="text-xs">Tarkoitus</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger data-testid={`call-purpose-${contact.id}`} className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{contact.allowed_purposes.map((p) => <SelectItem key={p} value={p}>{cap(p)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground">Palvelin varmentaa numeron sallittujen listalta ja luo kertakäyttöisen hyväksynnän.</p>
        </div>
        <DialogFooter>
          <button type="button" data-testid={`call-confirm-${contact.id}`} onClick={call} disabled={busy}
            className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground flex items-center justify-center">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Hyväksy ja soita"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Contacts() {
  const { data, loading, error, refetch } = useData(getContacts);

  const toggle = async (c) => {
    try {
      await updateContact(c.id, { enabled: !c.enabled });
      await refetch();
    } catch (requestError) {
      toast.error(userError(requestError, "Yhteystiedon tilan muuttaminen epäonnistui."));
    }
  };
  const remove = async (id) => { await deleteContact(id); toast.success("Yhteystieto poistettu."); refetch(); };

  return (
    <div>
      <PageHeader icon={Phone} title="Yhteystiedot & luvat" subtitle="Henkilöt, joille assistentti saa soittaa."
        action={<AddContactDialog onCreated={refetch} />} />

      <div className="mb-4 flex gap-2 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-3 text-xs text-emerald-500" data-testid="allowlist-banner">
        <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
        <p>Estetty kaikkien listaamattomien numeroiden puhelut ja automaattimaksut. Vain omistaja voi muokata tätä listaa — ei tekoäly eikä saapuvat viestit.</p>
      </div>

      {loading ? <LoadingCards /> : error ? <ErrorState onRetry={refetch} /> :
        data.length === 0 ? <EmptyState icon={Phone} title="Ei sallittuja yhteystietoja" hint="Lisää vain henkilöt, joille assistentti saa soittaa." /> : (
          <div className="space-y-3" data-testid="contacts-list">
            {data.map((c) => (
              <div key={c.id} className="rounded-2xl border border-border bg-card p-4 fade-up" data-testid={`contact-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">{c.name}</p>
                    <p className="font-mono text-sm text-primary">{c.phone_e164}</p>
                    <span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{c.role}</span>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Switch data-testid={`contact-toggle-${c.id}`} checked={c.enabled} onCheckedChange={() => toggle(c)} />
                    <span className={cn("text-[10px] font-medium", c.enabled ? "text-emerald-500" : "text-muted-foreground")}>{c.enabled ? "Käytössä" : "Pois käytöstä"}</span>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.allowed_purposes.length === 0 ? (
                    <span className="text-[11px] text-muted-foreground">Ei sallittuja tarkoituksia</span>
                  ) : c.allowed_purposes.map((p) => (
                    <span key={p} className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{cap(p)}</span>
                  ))}
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <CallDialog contact={c} />
                  <ConfirmDeleteButton
                    testid={`contact-delete-${c.id}`}
                    ariaLabel={`Poista yhteystieto ${c.name}`}
                    title="Poistetaanko sallittu yhteystieto?"
                    description={`Yhteystieto ”${c.name}” poistetaan sallittujen puhelujen listalta. Tätä ei voi perua.`}
                    onConfirm={() => remove(c.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
